import { useTranslation } from "react-i18next";
import { TbX } from "react-icons/tb";

// German strings are the stored values (DB-compatible); keys map to i18n entries
export const SPECIALTY_OPTIONS = [
    "Allgemeinmedizin",
    "Anästhesiologie",
    "Arbeitsmedizin",
    "Augenheilkunde",
    "Chirurgie",
    "Frauenheilkunde und Geburtshilfe",
    "Hals-Nasen-Ohrenheilkunde",
    "Haut- und Geschlechtskrankheiten",
    "Humangenetik",
    "Hygiene und Umweltmedizin",
    "Innere Medizin",
    "Kinder- und Jugendmedizin",
    "Kinder- und Jugendpsychiatrie und -psychotherapie",
    "Laboratoriumsmedizin",
    "Mikrobiologie, Virologie und Infektionsepidemiologie",
    "Mund-Kiefer-Gesichtschirurgie",
    "Neurochirurgie",
    "Neurologie",
    "Nuklearmedizin",
    "Öffentliches Gesundheitswesen",
    "Orthopädie und Unfallchirurgie",
    "Pathologie",
    "Pharmakologie",
    "Phoniatrie und Pädaudiologie",
    "Physikalische und Rehabilitative Medizin",
    "Physiologie",
    "Plastische und Ästhetische Chirurgie",
    "Psychiatrie und Psychotherapie",
    "Psychosomatische Medizin und Psychotherapie",
    "Radiologie",
    "Rechtsmedizin",
    "Strahlentherapie",
    "Transfusionsmedizin",
    "Urologie",
    "Zahnmedizin",
];

const SPECIALTY_KEY: Record<string, string> = {
    "Allgemeinmedizin":                                          "allgemeinmedizin",
    "Anästhesiologie":                                           "anaesthesiology",
    "Arbeitsmedizin":                                            "occupationalMedicine",
    "Augenheilkunde":                                            "ophthalmology",
    "Chirurgie":                                                 "surgery",
    "Frauenheilkunde und Geburtshilfe":                          "gynecology",
    "Hals-Nasen-Ohrenheilkunde":                                 "ent",
    "Haut- und Geschlechtskrankheiten":                          "dermatology",
    "Humangenetik":                                              "humanGenetics",
    "Hygiene und Umweltmedizin":                                 "hygiene",
    "Innere Medizin":                                            "internalMedicine",
    "Kinder- und Jugendmedizin":                                 "pediatrics",
    "Kinder- und Jugendpsychiatrie und -psychotherapie":         "childPsychiatry",
    "Laboratoriumsmedizin":                                      "laboratoryMedicine",
    "Mikrobiologie, Virologie und Infektionsepidemiologie":      "microbiology",
    "Mund-Kiefer-Gesichtschirurgie":                             "maxillofacialSurgery",
    "Neurochirurgie":                                            "neurosurgery",
    "Orthopädie und Unfallchirurgie":                            "orthopedics",
    "Neurologie":                                                "neurology",
    "Nuklearmedizin":                                            "nuclearMedicine",
    "Öffentliches Gesundheitswesen":                             "publicHealth",
    "Pathologie":                                                "pathology",
    "Pharmakologie":                                             "pharmacology",
    "Phoniatrie und Pädaudiologie":                              "phoniatrics",
    "Physikalische und Rehabilitative Medizin":                  "rehabilitationMedicine",
    "Physiologie":                                               "physiology",
    "Plastische und Ästhetische Chirurgie":                      "plasticSurgery",
    "Psychiatrie und Psychotherapie":                            "psychiatry",
    "Psychosomatische Medizin und Psychotherapie":               "psychosomatics",
    "Radiologie":                                                "radiology",
    "Rechtsmedizin":                                             "forensicMedicine",
    "Strahlentherapie":                                          "radiotherapy",
    "Transfusionsmedizin":                                       "transfusionMedicine",
    "Urologie":                                                  "urology",
    "Zahnmedizin":                                               "dentistry",
};

interface SpecialtyPickerProps {
    value: string[];
    onChange: (v: string[]) => void;
}

export function SpecialtyPicker({ value, onChange }: SpecialtyPickerProps) {
    const { t } = useTranslation();
    const normalized = Array.isArray(value) ? value : value ? [value as unknown as string] : [];

    const label = (s: string) => {
        const key = SPECIALTY_KEY[s];
        return key ? t(`specialties.${key}`, { defaultValue: s }) : s;
    };

    const available = SPECIALTY_OPTIONS
        .filter((s) => !normalized.includes(s))
        .sort((a, b) => label(a).localeCompare(label(b)));

    const add = (specialty: string) => {
        if (specialty && !normalized.includes(specialty)) {
            onChange([...normalized, specialty]);
        }
    };

    const remove = (specialty: string) => {
        onChange(normalized.filter((s) => s !== specialty));
    };

    return (
        <div className="specialty-picker">
            <select
                className="settings-select"
                value=""
                onChange={(e) => { add(e.target.value); e.target.value = ""; }}
            >
                <option value="">{t("settings.selectSpecialty")}</option>
                {available.map((s) => (
                    <option key={s} value={s}>{label(s)}</option>
                ))}
            </select>
            {normalized.length > 0 && (
                <div className="specialty-picker__tags">
                    {normalized.map((s) => (
                        <span key={s} className="specialty-picker__tag">
                            {label(s)}
                            <button
                                className="specialty-picker__tag-remove"
                                onClick={() => remove(s)}
                                aria-label={`Remove ${s}`}
                            >
                                <TbX />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
